import { inject, makeEnvironmentProviders } from '@angular/core';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { firstValueFrom, map } from 'rxjs';

import { MatDialog } from '@angular/material/dialog';

import { MemberProfilesService } from '@hm/core/services/member-profiles.service';
import { PrescriptionsService } from '@hm/core/services/prescriptions.service';
import { selectUser } from '@hm/core/stores/hm/hm.selectors';
import { clearPrescriptions, clearTargetPrescription } from '@hm/core/stores/prescriptions/prescriptions.actions';

import { CreateAppointmentModalComponent } from '@hm/ui/modals/create-appointment-modal/create-appointment-modal.component';

import { environment } from '@hm/environments/environment';

import { provideChatState } from '@lib/chat/core/providers/chat-state.provider';
import { ChatCommunicationService } from '@lib/chat/core/services/chat-communication.service';
import { ChatNavigationService } from '@lib/chat/core/services/chat-navigation.service';
import { CHAT_NAVIGATION_HANDLERS, type ChatNavigationHandlers } from '@lib/chat/core/tokens/chat-navigation.tokens';
import { CHAT_API_URL, CHAT_LIB_CONFIG } from '@lib/chat/core/tokens/chat.tokens';
import { ChatLibConfig, ThreadData } from '@lib/chat/models/chat-config.interface';

import { DEFAULT_PAGE_SIZE } from '@lib/commons/constants/request.const';

import { PrescriptionResponse, PrescriptionsSearch } from '@lib/core/models/POC-prescription.interface';
import { ActionResponseBody } from '@lib/core/models/action.interface';
import { AppointmentEventPlaceType, CreateAppointmentModalType } from '@lib/core/models/create-appointment.interface';
import { MemberProfile } from '@lib/core/models/member.interface';
import { Prescription, PrescriptionTypes } from '@lib/core/models/prescription.interface';
import { AppointmentTypes } from '@lib/core/models/visit.interface';

export function provideChatLibConfig() {
  return makeEnvironmentProviders([
    { provide: CHAT_API_URL, useValue: environment.healthManagerApiUrl },
    {
      provide: CHAT_LIB_CONFIG,
      useFactory: (): ChatLibConfig => {
        const memberProfilesService = inject(MemberProfilesService);
        const chatApiService = inject(ChatCommunicationService);
        const prescriptionsService = inject(PrescriptionsService);
        const store = inject(Store);
        const hmProfile = store.selectSignal(selectUser);

        return {
          showMedicalHistory: true,
          hasInternalProfile: true,
          ownUserInfo: {
            userId: hmProfile().userId,
            senderName: hmProfile().fullName,
          },
          notificationConfig: {
            title: 'У вас нове повідомлення',
            icon: 'dist/assets/images/logo.svg',
            soundEnabled: true,
          },
          resolveThreadData: async (threadId: string) => {
            try {
              const thread = await firstValueFrom(chatApiService.getThread(threadId));
              const prescriptionSearchPayload: PrescriptionsSearch = {
                page: 1,
                pageSize: DEFAULT_PAGE_SIZE,
                sortField: 'prescriptionCreationDate',
                sortOrder: 'Descending',
                userId: thread.ownerId,
              };
              let memberProfileInfo: MemberProfile | null = null;
              let memberPrescriptions: PrescriptionResponse[] = [];
              let memberComments: ActionResponseBody[] = [];

              try {
                memberProfileInfo = await firstValueFrom(memberProfilesService.getClientProfileByUserId(thread.ownerId));

                if (memberProfileInfo) {
                  const [prescriptions, comments] = await Promise.all([
                    firstValueFrom(prescriptionsService.POCPrescriptionsSearch(prescriptionSearchPayload).pipe(map(res => res.value))),
                    firstValueFrom(memberProfilesService.getClientProfileComments(memberProfileInfo.id).pipe(map(res => res.value))),
                  ]);
                  memberPrescriptions = prescriptions;
                  memberComments = comments;
                }
              } catch (memberError) {
                console.warn('Could not load member data, continuing with thread only:', memberError);
              }

              return { thread, memberProfileInfo, memberPrescriptions, memberComments } as ThreadData;
            } catch (error) {
              console.error('Error resolving thread data:', error);
              throw error;
            }
          },
        };
      },
      deps: [],
    },
    {
      provide: CHAT_NAVIGATION_HANDLERS,
      useFactory: (
        router: Router,
        dialog: MatDialog,
        store: Store,
        memberProfilesService: MemberProfilesService,
        chatNavigationService: ChatNavigationService,
        prescriptionsService: PrescriptionsService
      ): ChatNavigationHandlers => ({
        navigateToPrescription: (prescriptionId: string, userId: string) => {
          memberProfilesService.getClientProfileByUserId(userId).subscribe(memberProfile => {
            store.dispatch(clearTargetPrescription());
            store.dispatch(clearPrescriptions());
            router.navigate([`/members/${memberProfile.id}/guarantees`], {
              queryParams: { prescription: prescriptionId },
            });
          });
        },

        switchToPrescriptionTab: (prescriptionId: string) => {
          chatNavigationService.switchToPrescriptionTab(prescriptionId);
        },

        openCreateManualAppointmentModal: async (userId: string, modalType: CreateAppointmentModalType, metadata?) => {
          const prescriptionId = metadata?.prescriptionId;
          const getAppointmentType = (prescriptionType: PrescriptionTypes): AppointmentTypes => {
            switch (prescriptionType) {
              case PrescriptionTypes.Drugs:
                return AppointmentTypes.Drugs;
              case PrescriptionTypes.InstrumentalDiagnostic:
                return AppointmentTypes.InstrumentalDiagnostic;
              case PrescriptionTypes.LaboratoryService:
                return AppointmentTypes.LaboratoryService;
              default:
                return AppointmentTypes.Appointment;
            }
          };
          let prescription: Prescription | null = null;

          if (prescriptionId) {
            try {
              prescription = await firstValueFrom(prescriptionsService.getPrescriptionById(prescriptionId));
            } catch (error) {
              console.error('Error fetching prescription data:', error);
            }
          }
          console.log('prescription: ', prescription);

          dialog.open(CreateAppointmentModalComponent, {
            data: {
              userId: userId,
              type: modalType,
              appointmentType: prescription ? getAppointmentType(prescription.type) : AppointmentTypes.Appointment,
              appointmentName: prescription?.name || metadata?.prescriptionName,
              eventPlace: AppointmentEventPlaceType.Hospital,
              prescriptions: [
                {
                  id: prescriptionId,
                  franchise: prescription?.franchise ?? 0,
                },
              ],
              partnerId: metadata?.partnerId,
              locationId: metadata?.locationId,
            },
            width: '800px',
            maxHeight: '90vh',
          });
        },

        navigateToMemberProfile: (userId: string) => {
          memberProfilesService.getClientProfileByUserId(userId).subscribe(memberProfile => {
            router.navigate([`/members/${memberProfile.id}/profile`]);
          });
        },
      }),
      deps: [Router, MatDialog, Store, MemberProfilesService, ChatNavigationService, PrescriptionsService],
    },
    provideChatState(),
  ]);
}
